<template>
  <div
    :id="target"
    class="dynamic-yield-target"
  />
  <teleport
    v-if="campaign && isValidTemplate"
    :to="`#${target} .dy_unit`"
  >
    <component
      :is="`dynamic-yield-${validNames[campaign.data.template]}`"
      :campaign="campaign"
      @loaded="handleLoaded"
    />
  </teleport>
</template>

<script>

/**
 * Vue: Dynamic yield template (dynamic-yield-template)
 * -----------------------------------------------------------------------------
 * Dynamic Yield template.
 * @param {Object} campaign - DY campaign data including optional Shopify
 * section data.
 *
 */
// eslint-disable-next-line import/named
import { defineAsyncComponent } from 'vue'
import { mapActions, mapState } from 'vuex'

export default {
  name: 'DynamicYieldTemplate',

  components: {
    DynamicYieldRecommendationsCarousel: defineAsyncComponent({
      loader: () => import(
        // eslint-disable-next-line max-len
        /* webpackChunkName: 'component.dynamic-yield-recommendations-carousel' */
        '~async/dynamic-yield-template/dynamic-yield-recommendations-carousel'
      ),
    }),
    DynamicYieldRecommendationsOverlay: defineAsyncComponent({
      loader: () => import(
        // eslint-disable-next-line max-len
        /* webpackChunkName: 'component.dynamic-yield-recommendations-overlay' */
        '~async/dynamic-yield-template/dynamic-yield-recommendations-overlay'
      ),
    }),
    DynamicYieldStackedRecommendationsCarousels: defineAsyncComponent({
      loader: () => import(
        // eslint-disable-next-line max-len
        /* webpackChunkName: 'component.dynamic-yield-stacked-recommendations-carousels' */
        '~async/dynamic-yield-template/dynamic-yield-stacked-recommendations-carousels'
      ),
    }),
  },

  props: {
    target: {
      type: String,
      required: true,
    },
    settings: {
      type: Object,
      default: () => ({}),
    },
  },

  data() {
    return {
      validNames: {
        'section:product-recommendations': 'recommendations-carousel',
        'section:stacked-product-recommendations': 'stacked-recommendations-carousels',
        'overlay:product-recommendations': 'recommendations-overlay',
      },
      stylesheetNames: {
        'section:stacked-product-recommendations': 'dynamic-yield-stacked-recommendations-carousels',
        'overlay:product-recommendations': 'dynamic-yield-recommendations-overlay',
      },
    }
  },

  computed: {

    /**
     * Map Vuex state.
     */
    ...mapState({
      dyCampaigns: (state) => state['dynamic-yield'].campaigns,
      loadedCampaigns: (state) => state['dynamic-yield'].loaded,
    }),

    /**
     * The DY campaign for this section, including Shopify section data.
     * @returns {Object} The DY campaign data with Shopify section data.
     */
    campaign() {
      const dyCampaign = this.dyCampaigns[this.target] || null

      if (!dyCampaign) {
        return null
      }

      return {
        ...dyCampaign,
        section: this.settings,
      }
    },

    /**
     * The DY loaded state for this campaign.
     * @returns {Boolean}
     */
    loaded() {
      return this.loadedCampaigns[this.target] || false
    },

    /**
     * If the template is supported.
     * @returns {Boolean} Is valid
     */
    isValidTemplate() {
      return Boolean(this.campaign && this.validNames[this.campaign.data?.template])
    },

    /**
     * Template stylesheet name
     * @returns {String|null}
     */
    templateStylesheetName() {
      return (this.campaign && this.stylesheetNames[this.campaign.data?.template]) || null
    },
  },

  watch: {
    templateStylesheetName: {
      handler(newTemplateStylesheetName) {
        if (!newTemplateStylesheetName) {
          return
        }

        this.loadStylesheet(newTemplateStylesheetName)
      },
      immediate: true,
    },
  },

  methods: {

    /**
      * Vuex mapped actions.
     */
    ...mapActions({
      loadStylesheet: 'components/loadStylesheet',
    }),

    /**
     * Register the template element with DY
     * @param {HTMLElement} element - The template's root element
     */
    handleLoaded(element) {
      window.DYO?.recommendations.registerElements(element)
    },
  },
}
</script>
